import React from 'react'
import {
    WemoloButton,
    WemoloCheckbox,
    WemoloDownloadIcon,
    WemoloSendIcon,
    WemoloTypography
} from '../../../components/Wemolo'
import { DETECTION_EXPLORER_TEXT } from '../config'
import styles from './ActionBar.module.css'

export type ActionBarUIProps = {
    viewingFrom: number
    viewingTo: number
    total: number
    unitLabel: string
    // Filtered (accumulating) mode: show "Showing N · scanned X of Y" instead of "Viewing X–Y of Z".
    filtering: boolean
    shownCount: number
    scannedCount: number
    serverTotal: number
    selectedCount: number
    isAllSelected: boolean
    onSelectAllChange: (checked: boolean) => void
    onClearSelection: () => void
    showRetrieve: boolean
    retrieveEnabled: boolean
    isRetrieving: boolean
    exportEnabled: boolean
    isExporting: boolean
    onRetrieveClick: () => void
    onExportClick: () => void
    // Pagination lives in the centre of the bar, between the count and the actions.
    pagination?: React.ReactNode
}

export const ActionBarUI: React.FC<ActionBarUIProps> = ({
    viewingFrom,
    viewingTo,
    total,
    unitLabel,
    filtering,
    shownCount,
    scannedCount,
    serverTotal,
    selectedCount,
    isAllSelected,
    onSelectAllChange,
    onClearSelection,
    showRetrieve,
    retrieveEnabled,
    isRetrieving,
    exportEnabled,
    isExporting,
    onRetrieveClick,
    onExportClick,
    pagination
}) => (
    <div className={styles.actionBar}>
        <div className={styles.left}>
            <WemoloCheckbox checked={isAllSelected} onChange={(e) => onSelectAllChange(e.target.checked)} />
            <WemoloTypography variant="body-small" tag="span" className={styles.count}>
                {selectedCount > 0 ? (
                    <>
                        <span className={styles.countNumber}>{selectedCount}</span>{' '}
                        {DETECTION_EXPLORER_TEXT.actionBar.itemsSelected}
                    </>
                ) : filtering ? (
                    <>
                        {DETECTION_EXPLORER_TEXT.actionBar.showing}{' '}
                        <span className={styles.countNumber}>{shownCount}</span> ·{' '}
                        {DETECTION_EXPLORER_TEXT.actionBar.scanned}{' '}
                        <span className={styles.countNumber}>{scannedCount}</span>{' '}
                        {DETECTION_EXPLORER_TEXT.actionBar.of} {serverTotal}
                        {unitLabel}
                    </>
                ) : (
                    <>
                        {DETECTION_EXPLORER_TEXT.actionBar.viewing}{' '}
                        <span className={styles.countNumber}>
                            {viewingFrom}–{viewingTo}
                        </span>{' '}
                        {DETECTION_EXPLORER_TEXT.actionBar.of} {total}
                        {unitLabel}
                    </>
                )}
            </WemoloTypography>
            {selectedCount > 0 && (
                <WemoloButton size="small" onClick={onClearSelection} className={styles.clearSelection}>
                    {DETECTION_EXPLORER_TEXT.actionBar.clearSelection}
                </WemoloButton>
            )}
        </div>

        {pagination && <div className={styles.center}>{pagination}</div>}

        <div className={styles.right}>
            {showRetrieve && (
                <WemoloButton
                    view="primary"
                    size="small"
                    icon={<WemoloDownloadIcon />}
                    disabled={!retrieveEnabled}
                    onClick={onRetrieveClick}
                >
                    {isRetrieving
                        ? DETECTION_EXPLORER_TEXT.actionBar.retrieving
                        : DETECTION_EXPLORER_TEXT.actionBar.retrieve}
                </WemoloButton>
            )}
            <WemoloButton
                view="secondary"
                size="small"
                icon={<WemoloSendIcon />}
                disabled={!exportEnabled}
                onClick={onExportClick}
            >
                {isExporting ? DETECTION_EXPLORER_TEXT.actionBar.exporting : DETECTION_EXPLORER_TEXT.actionBar.export}
            </WemoloButton>
        </div>
    </div>
)
